<template>
  <v-timeline-item
    :color="colorForItem(item)"
    :icon="iconForItem(item)"
    fill-dot
  >
    <confirmation-dialog
      v-if="resendEmailConfirmation"
      visible
      :title="$t('label.resend_message')"
      :message="resendEmailConfirmationMessage"
      @close="resendEmailConfirmation = false"
      @confirm="emit('resend-confirmation', item)"
    />
    <div class="history-card rounded-lg px-3 pt-2 pb-3 mr-4 mb-3">
      <div class="d-flex align-center flex-wrap mb-1">
        <span class="text-caption ">{{ item.date }}</span>
        <template v-if="item.originator">
          <span class="text-caption mx-1">•</span>
          <span class="text-caption text--secondary">{{ item.originator }}</span>
        </template>
        <v-btn
          v-if="false &&(item.subtype === HistoryItemSubtype.Email || item.subtype === HistoryItemSubtype.SMS)"
          class="ml-auto"
          outlined
          x-small
          text
          @click="resendEmailConfirmation = true"
        >
          {{ $t('button.resend') }}
        </v-btn>
      </div>

      <!-- main text -->
      <i18n
        tag="p"
        class="text-body-2 font-weight-medium text--primary mb-0"
        :path="item.textKey"
      >
        <template #event>
          {{ item.textParams?.eventName }}
        </template>
        <template #recipient>
          {{ item.textParams?.recipient }}
        </template>
        <template #status>
          {{ item.textParams?.status }}
        </template>
        <template #subject>
          {{ item.textParams?.subject }}
        </template>
        <template #body>
          {{ item.textParams?.body }}
        </template>
      </i18n>

      <!-- subitems -->
      <div
        v-if="item.subItems && item.subItems.length"
        class="history-card__subitems rounded mt-2 px-3 py-2"
      >
        <i18n
          v-for="(sitem, si) in item.subItems"
          :key="si"
          tag="div"
          :path="sitem.textKey"
          class="text-caption text--secondary"
        >
          <template #recipient>
            <span class="text--primary">{{ item.textParams?.recipient }}</span>
          </template>
          <template #status>
            <span class="text--primary">{{ item.textParams?.status }}</span>
          </template>
          <template #subject>
            <span class="text--primary">{{ item.textParams?.subject }}</span>
          </template>
          <template #body>
            <span class="text--primary">{{ item.textParams?.body }}</span>
          </template>
        </i18n>
      </div>

      <!-- changeset -->
      <ul
        v-if="item.changes && item.changes.length"
        class="text-caption mt-2 pl-4"
      >
        <i18n
          v-for="(change, chi) in item.changes"
          :key="chi"
          tag="li"
          :path="change.textKey"
        >
          <template #field>
            <span
              :class="[{
                'font-weight-medium':
                  change.textParams === undefined || change.textParams.value === undefined
              }]"
            >{{
              $t(change.fieldKey) }}</span>
          </template>
          <template #value>
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="change.textParams?.htmlValue"
              class="history-diff px-2 py-1 mt-1"
              v-html="change.textParams?.htmlValue"
            />
            <!-- eslint-enable -->
            <span
              v-else
              class="font-weight-medium"
            >{{ change.textParams?.value }}</span>
          </template>
          <template #newvalue>
            <span class="font-weight-medium">{{ change.textParams?.newValue }}</span>
          </template>
          <template #oldvalue>
            <span class="font-weight-medium">{{ change.textParams?.oldValue }}</span>
          </template>
          <template #htmlvalue />
        </i18n>
      </ul>
    </div>
  </v-timeline-item>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import {
  HistoryItem,
  HistoryItemFlag,
  HistoryItemSubtype,
  HistoryItemType,
} from '@/services/reservation-history';
import Reservation from '@/model/Reservation';
import i18nn from '@/plugins/i18n';

const props = defineProps({
  item: { type: Object as PropType<HistoryItem>, required: true },
  reservation: { type: Object as PropType<Reservation>, required: true },
});

const emit = defineEmits<{(e: 'resend-confirmation', item: HistoryItem): void
}>();

const resendEmailConfirmation = ref(false);

const resendEmailConfirmationMessage = computed(
  () => {
    const recipientAddress = props.item.subtype === HistoryItemSubtype.Email
      ? props.reservation.contact?.email : props.reservation.contact?.phone;

    const messageType = props.item.subtype === HistoryItemSubtype.Email
      ? (i18nn.t('code.message_type.email') as string).toLowerCase()
      : i18nn.t('code.message_type.sms');

    return i18nn.t('message.confirm_resend_email', [messageType, props.item.textParams?.eventName, recipientAddress]);
  },
);

const iconForItem = (item: HistoryItem): string => {
  switch (item.subtype) {
    case HistoryItemSubtype.Created:
      // eslint-disable-next-line no-bitwise
      if (item.flags & HistoryItemFlag.TypeOnline) { return 'mdi-earth-plus'; }
      return 'mdi-file-document-plus-outline';
    case HistoryItemSubtype.Modified:
      return 'mdi-file-document-edit-outline';
    case HistoryItemSubtype.Email:
      /* eslint-disable no-bitwise */
      if (item.flags & HistoryItemFlag.StatusDelivered) { return 'mdi-email-fast-outline'; }
      if (item.flags & HistoryItemFlag.StatusBounced) { return 'mdi-email-alert-outline'; }
      if (item.flags & HistoryItemFlag.StatusUndeliverable) { return 'mdi-email-alert-outline'; }
      if (item.flags & HistoryItemFlag.StatusSend) { return 'mdi-email-fast-outline'; }
      return 'mdi-email-alert-outline';
    /* eslint-enable no-bitwise */
    case HistoryItemSubtype.SMS:
      /* eslint-disable no-bitwise */
      if (item.flags & HistoryItemFlag.StatusDelivered) { return 'mdi-message-fast-outline'; }
      if (item.flags & HistoryItemFlag.StatusUndeliverable) { return 'mdi-message-alert-outline'; }
      if (item.flags & HistoryItemFlag.StatusSend) { return 'mdi-message-fast-outline'; }
      return 'mdi-message-alert-outline';
    /* eslint-enable no-bitwise */
    default:
      return '';
  }
};

const colorForItem = (item: HistoryItem): string => {
  switch (item.type) {
    case HistoryItemType.Record:
      return 'blue';
    case HistoryItemType.Message: {
      /* eslint-disable no-bitwise */
      if (item.flags & HistoryItemFlag.StatusDelivered) { return 'green'; }
      if (item.flags & HistoryItemFlag.StatusBounced) { return 'red'; }
      if (item.flags & HistoryItemFlag.StatusUndeliverable) { return 'red'; }
      if (item.flags & HistoryItemFlag.StatusSend) { return 'yellow'; }
      if (item.flags & HistoryItemFlag.StatusProcessing) { return 'yellow'; }
      return 'red';
      /* eslint-enable no-bitwise */
    }
    default:
      return 'blue';
  }
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables';

.history-card {
  border: 1px solid $grey-15-color;
  background-color: $grey-2-color;

  .theme--dark & {
    background-color: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }

  &__subitems {
    background-color: $grey-8-color;

    .theme--dark & {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
}

.history-diff {
  border-radius: 4px;
  background-color: #ffc24e55;

  .theme--dark & {
    background-color: #ffc24e33;
  }

  font-size: 0.8rem;
  line-height: 1.5;

  ins {
    background-color: #00A26F55;

    .theme--dark & {
      background-color: #00A26F33;
    }

    text-decoration: none;
    border-radius: 2px;
  }

  del {
    background-color: #FF002B55;

    .theme--dark & {
      background-color: #FF002B33;
    }

    border-radius: 2px;
  }
}
</style>
